import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AuthService } from "services/auth.service";
import { IRequestPasswordReset } from "models/church.types";
import logoCiel from "assets/img/Logo-ciel.jpg";

export const RequestResetPassword: React.FC = () => {
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation(); // Empeche la propagation de l'événement

    setError("");
    setSuccess(false);

    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      setError("Veuillez saisir votre adresse email.");
      return;
    }

    setLoading(true);

    const payload: IRequestPasswordReset = {
      mail: trimmedEmail,
    };

    try {
      await AuthService.requestPasswordReset(payload);
      setSuccess(true);
      setEmail("");
    } catch (err: any) {
      console.error("Erreur lors de la demande de réinitialisation :", err);

      // Extraction robuste du message d'erreur Spring Boot / JHipster / Axios
      let message = "Une erreur est survenue lors de l'envoi de l'email.";

      if (err?.response?.data) {
        const data = err.response.data;
        message =
          data.detail ||
          data.message ||
          data.title ||
          (typeof data === "string" ? data : message);
      } else if (err?.message) {
        message = err.message;
      }

      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 h-full">
      <div className="flex content-center items-center justify-center h-full">
        <div className="w-full lg:w-4/12 px-4">
          <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-lg bg-[#0F172A] border-0">
            {/* En-tête */}
            <div className="text-white text-center mt-10 font-bold px-6">
              <div className="w-20 h-20 bg-white text-[#253a5c] rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl border-4 border-slate-700">
                <img
                  src={logoCiel}
                  alt="Logo Église"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <h3 className="text-2xl tracking-wide uppercase">
                Réinitialisation
              </h3>
              <p className="text-slate-400 text-xs font-normal mt-2">
                Entrez votre email pour recevoir le lien
              </p>
            </div>

            <div className="flex-auto px-4 lg:px-10 py-10">
              {/* Message d'erreur */}
              {error && (
                <div className="mb-4 text-xs bg-red-500/20 border border-red-500 text-red-300 p-3 rounded font-medium flex items-center">
                  <i className="fas fa-exclamation-circle mr-2 text-base"></i>
                  <span>{error}</span>
                </div>
              )}

              {/* Message de succès */}
              {success && (
                <div className="mb-4 text-xs bg-emerald-500/20 border border-emerald-500 text-emerald-300 p-3 rounded font-medium flex items-center">
                  <i className="fas fa-check-circle mr-2 text-base"></i>
                  <span>
                    Si le compte existe, un e-mail a été envoyé avec les
                    instructions.
                  </span>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Champ Adresse Email */}
                <div className="relative w-full mb-6">
                  <div className="relative flex w-full flex-wrap items-stretch">
                    <span className="z-10 h-full leading-snug font-normal absolute text-center text-slate-400 bg-transparent rounded text-base items-center justify-center w-10 pl-3 py-4">
                      <i className="fas fa-envelope"></i>
                    </span>
                    <input
                      name="email"
                      type="email"
                      className="border-0 px-3 py-4 pl-12 placeholder-slate-400 text-slate-600 bg-white rounded text-sm shadow focus:outline-none focus:ring-2 focus:ring-[#D4AF37] w-full ease-linear transition-all duration-150"
                      placeholder="Adresse email du compte"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                {/* Bouton d'action */}
                <div className="text-center">
                  <button
                    className="bg-[#D4AF37] text-[#1e293b] active:bg-yellow-600 text-sm font-bold uppercase px-6 py-4 rounded shadow-lg hover:shadow-xl outline-none focus:outline-none w-full ease-linear transition-all duration-150 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <i className="fas fa-circle-notch fa-spin mr-2"></i> Envoi en cours...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-paper-plane mr-2"></i> Envoyer le lien
                      </>
                    )}
                  </button>
                </div>

                {/* Lien Retour */}
                <div className="text-center mt-6">
                  <Link
                    to="/auth/login"
                    className="text-slate-400 hover:text-white transition-all text-xs inline-flex items-center gap-2"
                  >
                    <i className="fas fa-arrow-left text-xs"></i>
                    <span>Retour à la connexion</span>
                  </Link>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RequestResetPassword;